/**
 * Shard House — Glass Header Search
 * Single handler for all search interactions.
 * No competing handlers — app.js search drawer handler removed.
 */
(function initSearch() {
  function setup() {
    const overlay = document.getElementById('sh-search-overlay');
    if (!overlay) return;

    const openers = document.querySelectorAll('[data-search-drawer-toggle], #search-drawer-toggle, #ShSearchToggle, .sh-search-toggle');
    const closeBtn = overlay.querySelector('.sh-search-overlay__close');
    const input = document.getElementById('sh-search-input');
    const headerInput = document.getElementById('sh-header-search-input');
    const headerCloseBtn = document.querySelector('.sh-header-search__close');
    const submitBtn = document.getElementById('sh-search-submit');
    const resultsContainer = overlay.querySelector('[data-sh-search-results]');
    const initialContainer = overlay.querySelector('[data-sh-search-initial]');
    const form = overlay.querySelector('.sh-search-overlay__form');
    const headerForm = document.querySelector('.sh-header-search');

    const debounceFn = (typeof debounce === 'function')
      ? debounce
      : (fn, wait) => {
        let t;
        return (...args) => {
          clearTimeout(t);
          t = setTimeout(() => fn.apply(this, args), wait);
        };
      };

    function getSearchBase() {
      try {
        if (window.theme && window.theme.routes && window.theme.routes.search_url) {
          return window.theme.routes.search_url;
        }
      } catch (e) {
        // ignore
      }
      return '/search';
    }

    const isDesktop = () => window.innerWidth >= 990;

    // Listen for programmatic open requests (e.g. from sh-mobile-search.js)
    document.addEventListener('sh-search-open-request', function () {
      if (!overlay.classList.contains('sh-search-overlay--active')) {
        openOverlay();
      }
    });

    // Re-query openers after DOM is fully settled (dynamic elements)
    function getOpeners() {
      return document.querySelectorAll('[data-search-drawer-toggle], #search-drawer-toggle, #ShSearchToggle, .sh-search-toggle');
    }

    async function openOverlay() {
      // Prevent multiple open calls
      if (overlay.classList.contains('sh-search-overlay--active')) {
        return;
      }

      overlay.classList.add('sh-search-overlay--active');
      document.body.classList.add('sh-search-open');

      // highlight active header link
      openers.forEach(btn => btn.classList.add('is-active'));

      // Sync search-specific dark-mode with the unified glass class
      // (sh-header-glass--dark is managed by ThemeHeader.updateGlassMode)
      // Preserve whatever glass mode is active — dark hero keeps dark glass,
      // light/solid header keeps light glass.
      function syncSearchDarkMode() {
        const headerEl = document.querySelector('.header');
        if (!headerEl) return;
        const isDark = headerEl.classList.contains('sh-header-glass--dark');
        headerEl.classList.toggle('sh-search-glass--dark', isDark);
        overlay.classList.toggle('sh-search-overlay--dark-mode', isDark);
      }

      syncSearchDarkMode();

      // Listen for scroll changes to update colors dynamically
      const scrollHandler = function () {
        if (overlay.classList.contains('sh-search-overlay--active')) {
          syncSearchDarkMode();
        }
      };
      window.addEventListener('scroll', scrollHandler, { passive: true });
      overlay._scrollHandler = scrollHandler;

      // add panel open class for animation (mobile only)
      overlay.querySelector('.sh-search-overlay__panel')?.classList.add('is-open');

      // Accessibility
      try {
        overlay.setAttribute('aria-hidden', 'false');
        document.getElementById('main-content')?.setAttribute('aria-hidden', 'true');
      } catch (err) { }

      // Focus: desktop → inline header input, mobile → overlay input
      // iOS/iPadOS requires .focus() in the synchronous click call-stack
      // to open the virtual keyboard — setTimeout breaks the gesture chain.
      if (isDesktop()) {
        if (headerInput) {
          // Force reflow so display:none→inline-flex is computed before focus
          headerInput.offsetHeight;
          headerInput.focus({ preventScroll: true });
        }
      } else {
        if (input) {
          input.offsetHeight;
          input.focus({ preventScroll: true });
        }
      }
      setupFocusTrap();
    }

    function closeOverlay() {
      overlay.classList.remove('sh-search-overlay--active');
      document.body.classList.remove('sh-search-open');
      openers.forEach(btn => btn.classList.remove('is-active'));
      overlay.querySelector('.sh-search-overlay__panel')?.classList.remove('is-open');
      overlay.classList.remove('sh-search-overlay--dark-mode');

      // Remove glass header class
      const headerEl = document.querySelector('.header');
      if (headerEl) headerEl.classList.remove('sh-search-glass--dark');

      // Clear inline header input
      if (headerInput) headerInput.value = '';

      // Remove scroll listener
      if (overlay._scrollHandler) {
        window.removeEventListener('scroll', overlay._scrollHandler);
        overlay._scrollHandler = null;
      }
      // restore aria and focus
      try {
        overlay.setAttribute('aria-hidden', 'true');
        document.getElementById('main-content')?.removeAttribute('aria-hidden');
      } catch (err) { }
      removeFocusTrap();
    }

    function performSearch() {
      if (!input) return;
      const q = input.value.trim();
      if (!q) return;

      const base = getSearchBase();
      const url = new URL(base, window.location.origin);
      url.searchParams.set('q', q);
      window.location.href = url.toString();
    }

    function resetResults() {
      if (resultsContainer) {
        resultsContainer.classList.remove('is-active', 'is-loading');
        resultsContainer.innerHTML = '';
      }
      if (initialContainer) {
        initialContainer.classList.remove('is-hidden');
      }
    }

    function renderSearchResults(markup) {
      if (!resultsContainer) return;
      resultsContainer.innerHTML = markup || '';
      resultsContainer.classList.add('is-active');
      resultsContainer.classList.remove('is-loading');
      if (initialContainer) {
        initialContainer.classList.add('is-hidden');
      }
    }

    function getSearchResults(searchTerm) {
      if (!resultsContainer) return;

      resultsContainer.classList.add('is-loading');

      fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,article,query&${encodeURIComponent('resources[limit]')}=10&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku&section_id=predictive-search`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.status);
          }
          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search')?.innerHTML;

          renderSearchResults(resultsMarkup);
        })
        .catch(() => {
          resultsContainer.classList.remove('is-loading');
        });
    }

    // === CLICK HANDLING ===
    const panel = overlay.querySelector('.sh-search-overlay__panel');

    // Click-to-open: all search toggle buttons
    openers.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (overlay.classList.contains('sh-search-overlay--active')) {
          closeOverlay();
        } else {
          openOverlay();
        }
      });
    });

    // Close: X button (overlay)
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        closeOverlay();
      });
    }

    // Close: click backdrop
    const backdrop = overlay.querySelector('.sh-search-overlay__backdrop');
    if (backdrop) {
      backdrop.addEventListener('click', function (e) {
        e.stopPropagation();
        closeOverlay();
      });
    }

    // Close: ESC key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('sh-search-overlay--active')) {
        closeOverlay();
      }
    });

    // Close: click anywhere outside the search (desktop + mobile)
    // The backdrop handles clicks on the dimmed area, but on desktop the
    // header sits above the overlay (z-index 100 > 99).  A document-level
    // click listener catches clicks that land on the header chrome, page
    // body, or any element outside the search input / form.
    document.addEventListener('click', function (e) {
      if (!overlay.classList.contains('sh-search-overlay--active')) return;

      // Let openers, close buttons, and result links handle themselves
      const currentOpeners = getOpeners();
      const isInsideOverlay = overlay.contains(e.target);
      const isInsideHeaderSearch = headerForm && headerForm.contains(e.target);
      const isOpener = Array.from(currentOpeners).some(btn => btn.contains(e.target));

      if (!isInsideOverlay && !isInsideHeaderSearch && !isOpener) {
        closeOverlay();
      }
    });

    // Close: focusout — when the inline header input loses focus to an
    // element outside the search UI (e.g. user tabs away or clicks a nav
    // link), close gracefully.  Delay allows focus to settle on the new
    // target before checking.
    if (headerInput) {
      headerInput.addEventListener('blur', function () {
        if (!overlay.classList.contains('sh-search-overlay--active')) return;
        setTimeout(function () {
          const active = document.activeElement;
          const currentOpeners = getOpeners();
          const stillInSearch = (overlay.contains(active))
            || (headerForm && headerForm.contains(active))
            || Array.from(currentOpeners).some(btn => btn.contains(active));
          if (!stillInSearch) {
            closeOverlay();
          }
        }, 120);
      });
    }

    // Close: focusout for mobile overlay input
    if (input) {
      input.addEventListener('blur', function () {
        if (!overlay.classList.contains('sh-search-overlay--active')) return;
        if (isDesktop()) return; // desktop uses headerInput blur above
        setTimeout(function () {
          const active = document.activeElement;
          const currentOpeners = getOpeners();
          const stillInSearch = overlay.contains(active)
            || Array.from(currentOpeners).some(btn => btn.contains(active));
          if (!stillInSearch) {
            closeOverlay();
          }
        }, 120);
      });
    }

    // === INLINE HEADER SEARCH (desktop glass mode) ===
    // Close: inline header × button
    if (headerCloseBtn) {
      headerCloseBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        closeOverlay();
      });
    }

    if (headerInput) {
      headerInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          const q = headerInput.value.trim();
          if (!q) return;
          const base = getSearchBase();
          const url = new URL(base, window.location.origin);
          url.searchParams.set('q', q);
          window.location.href = url.toString();
        }
      });
    }

    if (headerForm) {
      headerForm.addEventListener('submit', function (e) {
        e.preventDefault();
        const q = headerInput ? headerInput.value.trim() : '';
        if (!q) return;
        const base = getSearchBase();
        const url = new URL(base, window.location.origin);
        url.searchParams.set('q', q);
        window.location.href = url.toString();
      });
    }

    // Focus trap implementation
    let _previouslyFocused = null;
    let _focusHandler = null;
    function setupFocusTrap() {
      _previouslyFocused = document.activeElement;
      const focusable = overlay.querySelectorAll('a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      const focusableEls = Array.from(focusable).filter(el => el.offsetParent !== null);
      const firstFocusable = focusableEls[0];
      const lastFocusable = focusableEls[focusableEls.length - 1];

      _focusHandler = function (e) {
        if (e.key === 'Tab') {
          if (focusableEls.length === 0) {
            e.preventDefault();
            return;
          }
          if (e.shiftKey) {
            if (document.activeElement === firstFocusable) {
              e.preventDefault();
              lastFocusable.focus();
            }
          } else {
            if (document.activeElement === lastFocusable) {
              e.preventDefault();
              firstFocusable.focus();
            }
          }
        }
      };

      document.addEventListener('keydown', _focusHandler);
    }

    function removeFocusTrap() {
      if (_focusHandler) document.removeEventListener('keydown', _focusHandler);
      if (_previouslyFocused && typeof _previouslyFocused.focus === 'function') {
        _previouslyFocused.focus();
      }
      _focusHandler = null;
      _previouslyFocused = null;
    }

    // Input: predictive search
    if (input) {
      input.addEventListener('input', debounceFn(() => {
        const term = input.value.trim();
        if (!term) {
          resetResults();
          return;
        }
        getSearchResults(term);
      }, 250));
    }

    // Submit: arrow button
    if (submitBtn) {
      submitBtn.addEventListener('click', function (e) {
        e.preventDefault();
        performSearch();
      });
    }

    // Submit: Enter key
    if (input) {
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          performSearch();
        }
      });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        performSearch();
      });
    }

    resetResults();
  } // end setup()

  // Run immediately if DOM is ready, otherwise wait
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();